import type { CSSProperties } from "react";
import type { RadiusKind, ShadowKind, StatsConfig, Theme } from "../types";

/** Resolve theme to concrete background/foreground/border tokens. */
export function themeColors(theme: Theme, dark: boolean) {
  if (theme === "dark" || (theme === "auto" && dark)) {
    return {
      bg: "#070708",
      fg: "#FFFFFF",
      cardBg: "#15151A",
      cardBorder: "rgba(255,255,255,0.08)",
      mutedFg: "rgba(255,255,255,0.55)",
      labelFg: "rgba(255,255,255,0.55)",
    };
  }
  if (theme === "transparent") {
    return {
      bg: "transparent",
      fg: "#070708",
      cardBg: "rgba(255,255,255,0.7)",
      cardBorder: "rgba(7,7,8,0.08)",
      mutedFg: "#7A7A82",
      labelFg: "#56565F",
    };
  }
  return {
    bg: "#FFFFFF",
    fg: "#070708",
    cardBg: "#FFFFFF",
    cardBorder: "#EDEDEF",
    mutedFg: "#A4A4AB",
    labelFg: "#56565F",
  };
}

export function radiusClass(r: RadiusKind): string {
  return r === "none" ? "rounded-none" : r === "md" ? "rounded-lg" : "rounded-2xl";
}

export function shadowClass(s: ShadowKind, isTransparent: boolean): string {
  if (s === "none") return "";
  if (s === "elevated") return "shadow-[0_8px_28px_rgba(7,7,8,0.12),0_2px_4px_rgba(7,7,8,0.06)]";
  return isTransparent ? "" : "shadow-soft";
}

/**
 * Inject theme tokens as CSS custom properties on the root container so
 * embedders (or downstream CSS) can override them.
 */
export function rootCssVars(cfg: StatsConfig, dark: boolean): CSSProperties {
  const c = themeColors(cfg.theme, dark);
  const bg = cfg.bgOverride ?? c.bg;
  const fg = cfg.fgOverride ?? c.fg;
  return {
    ["--lm-bg" as string]: bg,
    ["--lm-fg" as string]: fg,
    ["--lm-card-bg" as string]: c.cardBg,
    ["--lm-card-border" as string]: c.cardBorder,
    ["--lm-muted" as string]: c.mutedFg,
    ["--lm-label" as string]: c.labelFg,
    ["--lm-accent" as string]: cfg.accent,
    background: "var(--lm-bg)",
    color: "var(--lm-fg)",
  };
}
